import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { BehaviorSubject, Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreateExperienceWidget,
    CreateExperienceWidgetSuccess,
    DeleteExperienceWidget,
    DeleteExperienceWidgetFailure,
    DeleteExperienceWidgetSuccess,
    FetchExperienceWidgets,
    FetchExperienceWidgetsFailure,
    FetchExperienceWidgetsSuccess,
    UnsetExperienceWidgets,
    UpdateExperienceWidget,
    UpdateExperienceWidgetFailure,
    UpdateExperienceWidgetSuccess,
} from '../actions/index';
import { ExperienceWidget } from '../models/experience-widget.model';
import { ExperienceWidgetState } from '../models/experience-widget.state';
import { ExperienceWidgetsState } from '../models/experience-widgets.state';

@Injectable()
export class ExperienceWidgetService {
    @Select(ExperienceWidgetState.getEntities)
    experienceWidget$: Observable<ExperienceWidget[]>;

    @Select(ExperienceWidgetsState.getFilteredEntities)
    filteredExperienceWidget$: Observable<ExperienceWidget[]>;

    private _newExperienceWidgetSubject = new BehaviorSubject<ExperienceWidget>(null);
    public newExperienceWidgetSubject$ = this._newExperienceWidgetSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    getExperienceWidgets(): ExperienceWidget[] {
        return this._store.selectSnapshot(ExperienceWidgetsState.getFilteredEntities);
    }

    getExperienceWidgetById(id: string): ExperienceWidget {
        return ExperienceWidgetState.getEntity(this._store.selectSnapshot(ExperienceWidgetState), id);
    }

    async fetchExperienceWidgets(experienceId: string): Promise<boolean> {
        this._store.dispatch(new FetchExperienceWidgets({ experienceId }));
        return await this._actionHelper.race(FetchExperienceWidgetsSuccess, FetchExperienceWidgetsFailure).toPromise();
    }

    createExperienceWidget(experience: ExperienceWidget): any {
        this._store.dispatch(new CreateExperienceWidget(experience));

        this._action$.pipe(ofActionDispatched(CreateExperienceWidgetSuccess), take(1)).subscribe((action: CreateExperienceWidgetSuccess) => {
            this._newExperienceWidgetSubject.next(action.payload);
        });
    }

    clearExperienceWidgetsState(): void {
        this._store.dispatch(new UnsetExperienceWidgets());
    }

    updateExperienceWidget(experience: ExperienceWidget): void {
        this._store.dispatch(new UpdateExperienceWidget(experience));

        this._action$.pipe(ofActionDispatched(UpdateExperienceWidgetSuccess), take(1)).subscribe((action: UpdateExperienceWidgetSuccess) => {
            this._newExperienceWidgetSubject.next(action.payload);
        });
    }

    deleteExperienceWidget(experienceId: string): Promise<any> {
        this._store.dispatch(new DeleteExperienceWidget(experienceId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteExperienceWidgetSuccess),
                map((action: DeleteExperienceWidgetSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteExperienceWidgetFailure),
                map(() => null)
            )
        )
            .pipe()
            .toPromise();
    }
}
